import { InputText } from "primereact/inputtext";
import { useMemo, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";

/** The fields this control needs; callers may pass richer objects. */
export type PickerItem = {
  id: string;
  key: string;
  difficulty: string;
  points: number;
  category: string;
  title?: string;
};

type Props<T extends PickerItem> = {
  available: T[];
  selected: T[];
  onMoveToSelected: (item: T) => void;
  onMoveToAvailable: (item: T) => void;
  /** Reorder within the selected list, since challenge order is meaningful. */
  onReorderSelected: (fromId: string, toIndex: number) => void;
  /** Bulk moves. Select-all applies to what is currently visible, so it
      respects the category filter and the search box. */
  onSelectAll: (items: T[]) => void;
  onClearAll: () => void;
  /** Items the caller keeps at the top of both lists. They can still move
      between columns, but are not draggable, since any reorder would snap
      straight back. */
  isPinned?: (item: T) => boolean;
  /** Tooltip explaining why a pinned item cannot be moved. */
  pinnedHint?: string;
};

const MIME = "application/x-ctf-challenge";

/**
 * Replaces PrimeReact's PickList, which has no drag-and-drop of any kind.
 * Items can be dragged between the two columns, reordered inside Selected,
 * or moved with the keyboard-reachable button on each row: dragging alone
 * would leave the control unusable without a mouse.
 */
export default function ChallengePicker<T extends PickerItem>({
  available,
  selected,
  onMoveToSelected,
  onMoveToAvailable,
  onReorderSelected,
  onSelectAll,
  onClearAll,
  isPinned,
  pinnedHint,
}: Props<T>) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [overList, setOverList] = useState<"available" | "selected" | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const filteredAvailable = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((c) =>
      `${c.title ?? ""} ${c.key} ${c.category}`.toLowerCase().includes(q)
    );
  }, [available, query]);

  function startDrag(e: DragEvent<HTMLLIElement>, item: T) {
    setDragId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(MIME, item.id);
    /* Some browsers refuse the drag without a text/plain payload. */
    e.dataTransfer.setData("text/plain", item.id);
  }

  function endDrag() {
    setDragId(null);
    setOverList(null);
    setOverIndex(null);
  }

  const isFromAvailable = (id: string) => available.some((c) => c.id === id);

  function dropOnSelected(e: DragEvent, index: number | null) {
    e.preventDefault();
    const id = e.dataTransfer.getData(MIME) || dragId;
    if (!id) return endDrag();

    if (isFromAvailable(id)) {
      const item = available.find((c) => c.id === id);
      if (item) onMoveToSelected(item);
    } else if (index !== null) {
      onReorderSelected(id, index);
    }
    endDrag();
  }

  function dropOnAvailable(e: DragEvent) {
    e.preventDefault();
    const id = e.dataTransfer.getData(MIME) || dragId;
    if (id && !isFromAvailable(id)) {
      const item = selected.find((c) => c.id === id);
      if (item) onMoveToAvailable(item);
    }
    endDrag();
  }

  const row = (c: T, side: "available" | "selected", index: number) => {
    const pinned = isPinned?.(c) ?? false;
    return (
    <li
      key={c.id}
      draggable={!pinned}
      title={pinned ? pinnedHint : undefined}
      onDragStart={(e) => startDrag(e, c)}
      onDragEnd={endDrag}
      onDragOver={(e) => {
        if (side === "selected") {
          e.preventDefault();
          /* Nothing can go above a pinned row, so it shows no insert line. */
          setOverIndex(pinned ? null : index);
        }
      }}
      onDrop={(e) => {
        /* The column has its own drop handler (append to the end). Without
           this the drop ran twice: a reorder was immediately undone, and an
           item dragged in from Available was added twice. */
        e.stopPropagation();
        if (side === "selected") dropOnSelected(e, pinned ? index + 1 : index);
        else dropOnAvailable(e);
      }}
      onDoubleClick={() =>
        side === "available" ? onMoveToSelected(c) : onMoveToAvailable(c)
      }
      className={[
        "pick__item",
        pinned ? "pick__item--pinned" : "",
        dragId === c.id ? "pick__item--dragging" : "",
        side === "selected" && overIndex === index && dragId !== c.id
          ? "pick__item--over"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="pick__grip" aria-hidden="true">
        <i className={pinned ? "pi pi-thumbtack" : "pi pi-bars"} />
      </span>

      <span className="pick__body">
        <span className="pick__name">{c.title || c.key}</span>
        <span className="pick__meta">
          {c.difficulty} · {c.points} {t("admin.pts")} ·{" "}
          <span className="pick__cat">#{c.category}</span>
        </span>
      </span>

      <button
        type="button"
        className="pick__move"
        aria-label={
          side === "available" ? t("admin.selected") : t("admin.available1")
        }
        title={
          side === "available" ? t("admin.selected") : t("admin.available1")
        }
        onClick={() =>
          side === "available" ? onMoveToSelected(c) : onMoveToAvailable(c)
        }
      >
        <i
          className={
            side === "available" ? "pi pi-angle-right" : "pi pi-angle-left"
          }
        />
      </button>
    </li>
    );
  };

  return (
    <div className="pick">
      {/* ---- Available ---- */}
      <section
        className={`pick__col ${
          overList === "available" ? "pick__col--over" : ""
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setOverList("available");
        }}
        onDragLeave={() => setOverList(null)}
        onDrop={dropOnAvailable}
      >
        <header className="pick__head">
          <span className="pick__title">{t("admin.available1")}</span>
          <span className="pick__count">{filteredAvailable.length}</span>
          <button
            type="button"
            className="pick__bulk"
            onClick={() => onSelectAll(filteredAvailable)}
            disabled={filteredAvailable.length === 0}
          >
            {t("ui.selectAll")}
          </button>
        </header>

        <div className="pick__search">
          <span className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search" aria-hidden="true" />
            </span>
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("common.search")}
              aria-label={t("common.search")}
            />
          </span>
        </div>

        <ul className="pick__list">
          {filteredAvailable.map((c, i) => row(c, "available", i))}
          {filteredAvailable.length === 0 && (
            <li className="pick__empty">{t("ui.nothingHere")}</li>
          )}
        </ul>
      </section>

      {/* ---- Selected ---- */}
      <section
        className={`pick__col ${
          overList === "selected" ? "pick__col--over" : ""
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setOverList("selected");
        }}
        onDragLeave={() => setOverList(null)}
        onDrop={(e) => dropOnSelected(e, selected.length)}
      >
        <header className="pick__head">
          <span className="pick__title">{t("admin.selected")}</span>
          <span className="pick__count">{selected.length}</span>
          <button
            type="button"
            className="pick__bulk"
            onClick={onClearAll}
            disabled={selected.length === 0}
          >
            {t("ui.clearAll")}
          </button>
        </header>

        <ul className="pick__list pick__list--target">
          {selected.map((c, i) => row(c, "selected", i))}
          {selected.length === 0 && (
            <li className="pick__dropzone">
              <i className="pi pi-arrow-down" aria-hidden="true" />
              {t("ui.dropHere")}
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
